import { classifyClientErrorSource } from '@/lib/clientErrorClassification';
import { reportOpsSignal } from '@/lib/reportOpsSignal';
import { Endpoints } from '@/lib/Endpoints';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

type ClientErrorBoundary = 'global' | 'root';

function truncate(s: string | undefined, max: number): string | undefined {
  if (!s) return undefined;
  return s.length <= max ? s : `${s.slice(0, max)}…(truncated)`;
}

const REPORT_DEDUPE_TTL_MS = 10_000;
const recentReports = new Map<string, number>();

export function reportClientError(opts: {
  boundary: ClientErrorBoundary;
  error: Error & { digest?: string };
  /** Helps decode opaque "Script error." when browser exposes the originating file:line */
  errorEvent?: Pick<ErrorEvent, 'filename' | 'lineno' | 'colno' | 'type'>;
  /** Failed script/link/img URL (window error capture phase) */
  resourceUrl?: string;
  resourceTag?: 'script' | 'link' | 'img' | 'iframe' | 'unknown';
}) {
  if (typeof window === 'undefined') return;

  const dedupeKey = `${opts.boundary}::${opts.error?.message || ''}::${opts.resourceUrl || ''}`;
  const now = Date.now();
  const last = recentReports.get(dedupeKey) || 0;
  if (now - last < REPORT_DEDUPE_TTL_MS) return;
  recentReports.set(dedupeKey, now);

  const pageHost =
    typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : '';

  const anyErr = opts.error as any;

  // Axios/network failures are common on mobile webviews; include request context so backend logs are actionable.
  const axiosContext =
    anyErr && (anyErr.isAxiosError === true || anyErr.name === 'AxiosError')
      ? {
          axiosUrl: String(anyErr.config?.url || ''),
          axiosMethod: String(anyErr.config?.method || ''),
          axiosBaseURL: String(anyErr.config?.baseURL || ''),
          axiosTimeout: Number(anyErr.config?.timeout || 0) || 0,
          axiosStatus: Number(anyErr.response?.status || 0) || 0,
        }
      : null;

  const messageBase = opts.error?.message || 'Unknown error';
  const message =
    axiosContext && axiosContext.axiosUrl
      ? `${messageBase} (axios ${axiosContext.axiosMethod || 'GET'} ${axiosContext.axiosUrl})`
      : messageBase;

  const stack = opts.error?.stack;
  const srcFile = opts.errorEvent?.filename || '';

  const { bucket, hints } = classifyClientErrorSource({
    message,
    stack,
    srcFile,
    resourceUrl: opts.resourceUrl,
    pageHost,
  });

  const safeHints = hints
    .map((h) => truncate(String(h), 1000) || '')
    .filter(Boolean)
    .slice(0, 12);

  const safeResourceUrl = truncate(opts.resourceUrl, 4000);

  const payload = {
    boundary: opts.boundary,
    message,
    name: opts.error?.name,
    digest: opts.error?.digest,
    stack: truncate(stack, 6000),
    href: window.location?.href || '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    ts: new Date().toISOString(),
    viewportW: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportH: typeof window !== 'undefined' ? window.innerHeight : 0,
    /** first_party | third_party | unknown — from stack / URLs (for ads pixel triage) */
    sourceBucket: bucket,
    sourceHints: safeHints,
    ...(opts.errorEvent && {
      srcFile,
      srcLine: opts.errorEvent.lineno ?? 0,
      srcCol: opts.errorEvent.colno ?? 0,
      eventType: opts.errorEvent.type || '',
    }),
    ...(safeResourceUrl && {
      resourceUrl: safeResourceUrl,
      resourceTag: opts.resourceTag || 'unknown',
    }),
    ...(axiosContext && {
      axiosUrl: truncate(axiosContext.axiosUrl, 2000),
      axiosMethod: axiosContext.axiosMethod,
      axiosBaseURL: truncate(axiosContext.axiosBaseURL, 2000),
      axiosTimeout: axiosContext.axiosTimeout,
      axiosStatus: axiosContext.axiosStatus,
    }),
  };

  const body = JSON.stringify(payload);

  reportOpsSignal({
    kind: 'client_error',
    message: `${opts.boundary}: ${message}`,
    tags: { sourceBucket: bucket, boundary: opts.boundary },
  });

  const url = `${getApiBaseUrl()}${Endpoints.CLIENT_ERROR_REPORT}`;

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (sendBeaconErr) {
    console.error('[client-error] sendBeacon failed', sendBeaconErr);
  }

  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch((fetchErr) => {
    console.error('[client-error] fetch failed', fetchErr, payload);
  });
}
